import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    'modelSelector',
    'loadingIndicator',
    'noSelectionMessage',
    'dataContainer',
    'environmentHeaders',
    'modelData',
    'errorMessage',
    'errorText',
    'toggleOutOfSync',
    'row'
  ]

  static values = {
    availableModels: Array,
    selectedModel: String
  }

  // Store the current data for re-rendering after sync
  currentData = null

  connect() {
    console.log("Drift Detection Controller connected");

    // Restore "Show Only Out of Sync" checkbox state (default to true)
    const storedOutOfSync = localStorage.getItem('drift_detection_show_out_of_sync');
    // Default to checked (true) unless explicitly set to 'false'
    const showOutOfSync = storedOutOfSync !== 'false';
    if (this.hasToggleOutOfSyncTarget) {
      this.toggleOutOfSyncTarget.checked = showOutOfSync;
    }

    // Priority: URL param > localStorage > nothing
    const urlParams = new URLSearchParams(window.location.search);
    const urlModel = urlParams.get('model');
    const storedModel = localStorage.getItem('drift_detection_selected_model');

    let modelToLoad = urlModel || storedModel || this.selectedModelValue;

    if (modelToLoad && this.availableModelsValue.includes(modelToLoad)) {
      this.modelSelectorTarget.value = modelToLoad;
      this.fetchModelData(modelToLoad);
    }
  }

  onModelSelect(event) {
    const model = event.target.value;

    if (!model) {
      this.showNoSelection();
      this.updateUrlAndStorage(null);
      return;
    }

    this.updateUrlAndStorage(model);
    this.fetchModelData(model);
  }

  updateUrlAndStorage(model) {
    // Update URL
    const url = new URL(window.location);
    if (model) {
      url.searchParams.set('model', model);
      localStorage.setItem('drift_detection_selected_model', model);
    } else {
      url.searchParams.delete('model');
      localStorage.removeItem('drift_detection_selected_model');
    }
    window.history.replaceState({}, '', url);
  }

  showNoSelection() {
    this.noSelectionMessageTarget.style.display = 'block';
    this.dataContainerTarget.style.display = 'none';
    this.errorMessageTarget.style.display = 'none';
    this.currentData = null;
  }

  showLoading() {
    this.loadingIndicatorTarget.style.display = 'inline';
    this.noSelectionMessageTarget.style.display = 'none';
    this.errorMessageTarget.style.display = 'none';
  }

  hideLoading() {
    this.loadingIndicatorTarget.style.display = 'none';
  }

  showError(message) {
    this.hideLoading();
    this.noSelectionMessageTarget.style.display = 'none';
    this.dataContainerTarget.style.display = 'none';
    this.errorMessageTarget.style.display = 'block';
    this.errorTextTarget.textContent = message;
  }

  async fetchModelData(model) {
    this.showLoading();

    try {
      const response = await fetch(`/fanworld-domination/drift_detector/fetch?model=${encodeURIComponent(model)}`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      this.currentData = data;
      this.renderData(data);

    } catch (error) {
      console.error('Error fetching drift data:', error);
      this.showError(error.message || 'Failed to fetch data from environments');
    } finally {
      this.hideLoading();
    }
  }

  renderData(data) {
    const { environments, items, model } = data;

    // Show the data container
    this.noSelectionMessageTarget.style.display = 'none';
    this.errorMessageTarget.style.display = 'none';
    this.dataContainerTarget.style.display = 'block';

    // Render environment headers
    this.renderEnvironmentHeaders(environments);

    // Render model data
    this.renderModelData(model, items[model], environments);

    // Apply the "out of sync" filter if checkbox is checked
    if (this.hasToggleOutOfSyncTarget && this.toggleOutOfSyncTarget.checked) {
      this.applyOutOfSyncFilter(true);
    }
  }

  renderEnvironmentHeaders(environments) {
    const width = Math.floor(100 / (environments.length + 1));
    let html = '';

    environments.forEach(env => {
      html += `
        <div style="padding: 5px; width: ${width}%">
          <h2 style='text-align: center;'>${this.titleize(env)}</h2>
        </div>
      `;
    });

    this.environmentHeadersTarget.innerHTML = html;
  }

  renderModelData(model, entries, environments) {
    if (!entries || Object.keys(entries).length === 0) {
      this.modelDataTarget.innerHTML = `
        <div style="text-align: center; padding: 30px; color: #666;">
          <p>No records found for ${model}</p>
        </div>
      `;
      return;
    }

    const width = Math.floor(100 / (environments.length + 1));
    let html = `
      <div style="padding: 5px; width: 100%;">
        <h3 style='border-bottom: 1px solid gray; padding-bottom: 7px;'>${model}</h3>
      </div>
    `;

    Object.entries(entries).forEach(([key, envs]) => {
      html += `
        <div data-drift-detection-target='row'>
          <h4>${this.escapeHtml(key)}</h4>
          <div style='display: flex; flex-direction: row; justify-content: space-between;'>
      `;

      Object.entries(envs).forEach(([envName, envValues]) => {
        const hasData = envValues && Object.keys(envValues).length > 0;
        const isOutOfSync = envValues && envValues.out_of_sync;
        const isProduction = envName === 'production';

        let bgColor = 'white';
        if (hasData) {
          bgColor = isOutOfSync ? 'lightcoral' : 'lightgreen';
        }

        const outline = hasData && isProduction ? '3px solid darkgreen' : '';

        html += `
          <div style='width: ${width}%; background-color: ${bgColor}; outline: ${outline}; padding: 10px; margin-bottom: 10px;'>
        `;

        if (hasData) {
          // Render all fields
          Object.entries(envValues).forEach(([fieldKey, fieldValue]) => {
            html += `<p style='margin: 0;'><strong>${this.escapeHtml(fieldKey)}</strong> ${this.escapeHtml(String(fieldValue))}</p>`;
          });

          // Render copy buttons
          html += `<p style='margin-bottom: 10px; padding-top: 5px; margin-top: 2em; border-top: 1px solid black;'>Copy ${envName} to...</p>`;

          environments.filter(e => e !== envName).forEach(otherEnv => {
            const targetId = entries[key][otherEnv]?.id || '';
            // Base64 encode the object to safely pass it through HTML attributes
            const objectBase64 = btoa(unescape(encodeURIComponent(JSON.stringify(envValues))));

            html += `
              <button class="sync-button"
                      data-action='drift-detection#copyToEnv'
                      data-drift-detection-env-param="${otherEnv}"
                      data-drift-detection-id-param='${targetId}'
                      data-drift-detection-model-param='${model}'
                      data-drift-detection-object-param='${objectBase64}'>
                ${this.titleize(otherEnv)}
              </button>
            `;
          });
        }

        html += '</div>';
      });

      html += '</div></div>';
    });

    this.modelDataTarget.innerHTML = html;
  }

  async copyToEnv(event) {
    const button = event.currentTarget;
    const { env, object: objectBase64, ...rest } = event.params;

    // Decode the base64 encoded object
    const objectJson = decodeURIComponent(escape(atob(objectBase64)));
    const objectData = JSON.parse(objectJson);

    // Get the record identifier for the success message
    const recordName = objectData.slug || objectData.name || 'Record';

    // Disable the button and show loading state
    button.disabled = true;
    const originalText = button.textContent;
    button.textContent = 'Syncing...';

    try {
      const response = await fetch(`/fanworld-domination/drift_detector/${env}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ key: 'betterthanahotdogstand', object: objectData, ...rest })
      });

      if (response.ok) {
        // Re-fetch the model data to show updated sync status
        const currentModel = this.modelSelectorTarget.value;
        if (currentModel) {
          await this.fetchModelData(currentModel);
        }

        // Show success message after data reloads
        this.showToast('success', `Successfully synced "${recordName}" to ${this.titleize(env)}`);
      } else if (response.status === 401) {
        throw new Error('Unauthorized - please ensure you are logged in with a @fanword.com email');
      } else {
        throw new Error('Sync request failed');
      }
    } catch (error) {
      console.error('Error:', error);
      this.showToast('danger', error.message || 'There was an error copying the data to the environment');

      // Re-enable the button on error
      button.disabled = false;
      button.textContent = originalText;
    }
  }

  showOnlyOutOfSync(event) {
    const filterEnabled = event.target.checked;

    // Persist the checkbox state
    localStorage.setItem('drift_detection_show_out_of_sync', filterEnabled.toString());

    this.applyOutOfSyncFilter(filterEnabled);
  }

  applyOutOfSyncFilter(filterEnabled) {
    // Get all row targets (they are dynamically created, so we query directly)
    const rows = this.modelDataTarget.querySelectorAll('[data-drift-detection-target="row"]');

    if (filterEnabled) {
      rows.forEach(databaseEntry => {
        // Check if this row has any out-of-sync indicators (lightcoral) or missing (white) entries
        const hasOutOfSync = databaseEntry.querySelectorAll('div[style*="lightcoral"], div[style*="white"]').length > 0;
        databaseEntry.style.display = hasOutOfSync ? 'block' : 'none';
      });
    } else {
      rows.forEach(databaseEntry => {
        databaseEntry.style.display = 'block';
      });
    }
  }

  // Helper methods
  titleize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // Custom toast notification for Active Admin context
  // Uses fixed positioning to ensure visibility
  showToast(type, message) {
    // Remove any existing toasts from this controller
    document.querySelectorAll('.drift-detection-toast').forEach(el => el.remove());

    const colors = {
      success: { bg: '#d4edda', border: '#c3e6cb', text: '#155724' },
      info: { bg: '#d1ecf1', border: '#bee5eb', text: '#0c5460' },
      danger: { bg: '#f8d7da', border: '#f5c6cb', text: '#721c24' },
      warning: { bg: '#fff3cd', border: '#ffeeba', text: '#856404' }
    };

    const color = colors[type] || colors.info;

    const toast = document.createElement('div');
    toast.className = 'drift-detection-toast';
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 10000;
      padding: 15px 20px;
      background-color: ${color.bg};
      border: 1px solid ${color.border};
      color: ${color.text};
      border-radius: 5px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      font-size: 14px;
      max-width: 400px;
      animation: slideIn 0.3s ease-out;
    `;
    toast.textContent = message;

    // Add animation keyframes if not already present
    if (!document.getElementById('drift-toast-styles')) {
      const style = document.createElement('style');
      style.id = 'drift-toast-styles';
      style.textContent = `
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes fadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(toast);

    // Auto-remove after 4 seconds
    setTimeout(() => {
      toast.style.animation = 'fadeOut 0.5s ease-out';
      setTimeout(() => toast.remove(), 500);
    }, 4000);
  }
}
